// Q9 S5 / D13a — Per-section PATCH offline queue.
//
// Spec lock (docs/specs/visit-report-save-state.md §Offline handling):
//   "Queue entry shape extends to { sr_id, section: SectionName | "submit",
//    payload, queued_at, retry_count }. Replayer iterates in insertion
//    order, issuing the matching PATCH for each section entry."
//
// Why insertion-ordered (D13a) rather than per-section keyed (D13b):
//   "A carer who patches Vitals, then re-opens Vitals to edit, would
//    overwrite the first PATCH in store. If the first PATCH had data the
//    second doesn't (e.g., carer cleared a field), we'd lose it.
//    Insertion-ordered queue replays each PATCH faithfully; backend
//    last-write-wins per section settles the final state."
//
// IndexedDB store: `pending-section-patches`.
//
// Q9 S7 cleanup: bumped DB_VERSION 2 → 3 to drop the legacy
// `pending-reports` store (which fed the monolithic POST
// /v1/visit-reports retired in S2 / D18). The S5 onupgradeneeded
// preserved the legacy store for a one-quarter drain window;
// S7 now removes it. Returning carers whose device still has
// the v2 schema get an automatic deleteObjectStore on next open.

import type { SectionStep } from "./visit-report-conditional-rules.js";

export type QueueSection = SectionStep | "submit";

export interface PendingSectionPatch {
  id?:           number;        // auto-incremented IDB key — preserves insertion order
  sr_id:         string;
  section:       QueueSection;
  payload:       Record<string, unknown>;
  queued_at:     string;        // ISO timestamp
  retry_count:   number;
}

const DB_NAME           = "hcm-offline";
const STORE_NAME        = "pending-section-patches";
const LEGACY_STORE_NAME = "pending-reports";       // dropped in v3
const DB_VERSION        = 3;                       // S5 added v2 (new store); S7 drops legacy store

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e): void => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id", autoIncrement: true });
      }
      if (db.objectStoreNames.contains(LEGACY_STORE_NAME)) {
        db.deleteObjectStore(LEGACY_STORE_NAME);
      }
    };
    req.onsuccess = (): void => resolve(req.result);
    req.onerror   = (): void => reject(req.error);
  });
}

function tx<T>(
  db: IDBDatabase, mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE_NAME, mode);
    const req = fn(t.objectStore(STORE_NAME));
    req.onsuccess = (): void => resolve(req.result);
    req.onerror   = (): void => reject(req.error);
  });
}

/** Append a new section PATCH to the queue (insertion-ordered). */
export async function enqueueSectionPatch(entry: Omit<PendingSectionPatch, "id">): Promise<number> {
  const db = await openDb();
  try {
    return await tx<IDBValidKey>(db, "readwrite", (s) => s.add(entry)) as number;
  } finally {
    db.close();
  }
}

/** Read all queued PATCHes in insertion order (auto-increment id ascending). */
export async function getPendingSectionPatches(): Promise<PendingSectionPatch[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t   = db.transaction(STORE_NAME, "readonly");
    const req = t.objectStore(STORE_NAME).getAll();
    req.onsuccess = (): void => { db.close(); resolve(req.result as PendingSectionPatch[]); };
    req.onerror   = (): void => { db.close(); reject(req.error); };
  });
}

/** Delete a queued PATCH by its IDB key (called after successful replay). */
export async function deleteSectionPatch(id: number): Promise<void> {
  const db = await openDb();
  try {
    await tx(db, "readwrite", (s) => s.delete(id));
  } finally {
    db.close();
  }
}

/** Bump the retry_count for an entry that failed to replay. */
export async function bumpRetryCount(id: number): Promise<void> {
  const db = await openDb();
  try {
    const existing = await new Promise<PendingSectionPatch | undefined>((resolve, reject) => {
      const req = db.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).get(id);
      req.onsuccess = (): void => resolve(req.result as PendingSectionPatch | undefined);
      req.onerror   = (): void => reject(req.error);
    });
    if (!existing) return;
    existing.retry_count = (existing.retry_count ?? 0) + 1;
    await tx(db, "readwrite", (s) => s.put(existing));
  } finally {
    db.close();
  }
}

/** Count of pending section PATCHes (used by tests + telemetry). */
export async function pendingSectionPatchCount(): Promise<number> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t   = db.transaction(STORE_NAME, "readonly");
    const req = t.objectStore(STORE_NAME).count();
    req.onsuccess = (): void => { db.close(); resolve(req.result); };
    req.onerror   = (): void => { db.close(); reject(req.error); };
  });
}
